import { useState } from 'react';
import { toast } from 'sonner';

import { Option, Select } from '@/components/form/Select';
import { Modal } from '@/components/Modal';
import { useModifyAccount } from '@/hooks/useAccounts';
import { generateSeverityOptions } from '@/hooks/useIntegration';
import { CopyIconButton } from '@/sections/settings/components/primitives/CopyIconButton';
import { Account } from '@/types';

/**
 * Field types for notification configuration display
 */
export type NotificationFieldType = 'plain' | 'masked' | 'copyable';

/**
 * Configuration for a single field in the notification modal
 */
export interface NotificationField {
  /** Display label for the field */
  label: string;
  /** Value to display (or placeholder for masked fields) */
  value: string;
  /** How to render the field */
  type: NotificationFieldType;
  /** Tooltip text explaining why the field is masked/immutable */
  tooltip?: string;
  /** data-testid attribute for testing */
  testId?: string;
}

/**
 * Props for the NotificationViewModal base component
 */
export interface NotificationViewModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** The account to view/edit */
  account: Account;
  /** Modal title */
  title: string;
  /** Username for API call ('webhook', 'email', or chat provider like 'slack') */
  username: string;
  /** Fields to display in the modal */
  fields: NotificationField[];
  /** Success toast message */
  successMessage?: string;
  /** Error toast message */
  errorMessage?: string;
  /** data-testid for the modal */
  modalTestId?: string;
  /** data-testid for the save button */
  saveButtonTestId?: string;
  /** data-testid prefix for the severity select (will have '-select' appended) */
  severitySelectTestId?: string;
  /** Name attribute for the severity select element */
  severitySelectName?: string;
}

/**
 * NotificationViewModal - Base component for viewing notification configuration
 * and editing severity threshold.
 *
 * Shared features across all notification modals:
 * - Editable severity threshold dropdown
 * - Save/Cancel buttons with proper state management
 * - Automatic reset on cancel
 *
 * Used by:
 * - WebhookViewModal
 * - EmailViewModal
 * - ChatMessagingViewModal
 *
 * @example
 * // Webhook with masked fields
 * <NotificationViewModal
 *   open={open}
 *   onClose={onClose}
 *   account={webhook}
 *   title="Webhook Configuration"
 *   username="webhook"
 *   fields={[
 *     { label: 'Webhook URL', value: '*****', type: 'masked', tooltip: '...' },
 *   ]}
 * />
 *
 * @example
 * // Email with copyable field
 * <NotificationViewModal
 *   open={open}
 *   onClose={onClose}
 *   account={emailAccount}
 *   title="Email Configuration"
 *   username="email"
 *   fields={[
 *     { label: 'Email Address', value: emailAccount.value, type: 'copyable' },
 *   ]}
 * />
 */
export function NotificationViewModal({
  open,
  onClose,
  account,
  title,
  username,
  fields,
  successMessage = 'Settings updated',
  errorMessage = 'Failed to update settings',
  modalTestId,
  saveButtonTestId,
  severitySelectTestId,
  severitySelectName = 'severity',
}: NotificationViewModalProps) {
  // Get current severity from authoritative settings.
  const currentSeverity = account?.settings?.severityThreshold || 'MHC';

  // Local state for severity
  const [selectedSeverity, setSelectedSeverity] = useState(currentSeverity);

  // Mutation hook for updating settings
  const { mutateAsync: updateAccount, status } = useModifyAccount('update');
  const isPending = status === 'pending';

  // Check if severity has changed
  const hasChanged = selectedSeverity !== currentSeverity;

  // Generate severity options for dropdown
  const severityOptions: Option[] = generateSeverityOptions();

  /**
   * Handle severity change and save to backend
   */
  const handleSave = async () => {
    if (!hasChanged) return;

    try {
      await updateAccount({
        username,
        key: account.key,
        config: {}, // Required by LinkAccount type, not used by 'update' action
        settings: {
          severityThreshold: selectedSeverity,
        },
        silent: true, // Suppress default toast - we show custom message
      });

      toast.success(successMessage, {
        description: 'Severity threshold has been saved.',
      });

      onClose();
    } catch {
      // Revert to original value on error
      setSelectedSeverity(currentSeverity);
      toast.error(errorMessage);
      console.error(errorMessage);
    }
  };

  /**
   * Handle modal close - reset any pending changes
   */
  const handleClose = () => {
    setSelectedSeverity(currentSeverity);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="lg"
      footer={{
        text: isPending ? 'Saving...' : 'Save',
        onClick: handleSave,
        disabled: !hasChanged || isPending,
        'data-testid': saveButtonTestId,
      }}
      data-testid={modalTestId}
    >
      <div className="flex flex-col gap-6">
        {/* Render configurable fields */}
        {fields.map((field, index) => (
          <NotificationFieldRenderer key={index} field={field} />
        ))}

        {/* Severity Threshold - Always last, always editable */}
        <div className="flex flex-col gap-2">
          <label
            className="text-chariot-text-primary text-sm font-semibold"
            htmlFor={severitySelectName}
          >
            Severity Threshold
          </label>
          <Select
            name={severitySelectName}
            value={selectedSeverity}
            onChange={e => setSelectedSeverity(e.target.value)}
            options={severityOptions}
            disabled={isPending}
            data-testid={severitySelectTestId}
          />
          <p className="text-chariot-text-secondary text-xs">
            Select the minimum risk severity level at which Chariot should send
            notifications.
          </p>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Props for the NotificationFieldRenderer component
 */
interface NotificationFieldRendererProps {
  field: NotificationField;
}

/**
 * Renders a single field in the notification modal based on its type.
 *
 * Field types:
 * - 'plain': Simple text display without copy functionality
 * - 'copyable': Text with copy button and green flash feedback
 * - 'masked': Masked text (asterisks) with cursor-not-allowed styling
 */
function NotificationFieldRenderer({ field }: NotificationFieldRendererProps) {
  const { label, value, type, tooltip, testId } = field;

  if (type === 'plain') {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {label}
        </span>
        <span
          className="text-chariot-text-primary font-mono text-sm"
          data-testid={testId}
        >
          {value || 'Not configured'}
        </span>
      </div>
    );
  }

  if (type === 'copyable') {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {label}
        </span>
        <div className="flex items-center gap-2">
          <span
            className="text-chariot-text-primary font-mono text-sm"
            data-testid={testId}
          >
            {value || 'Not configured'}
          </span>
          {value && (
            <CopyIconButton
              value={value}
              label={label}
              data-testid={`copy-${label.toLowerCase().replace(/\s+/g, '-')}`}
            />
          )}
        </div>
      </div>
    );
  }

  if (type === 'masked') {
    return (
      <div className="flex flex-col gap-2">
        <span className="text-chariot-text-primary text-sm font-semibold">
          {label}
        </span>
        <div
          className="border-chariot-stroke bg-chariot-background-tertiary text-chariot-text-secondary cursor-not-allowed rounded-md border px-3 py-2 font-mono text-sm"
          title={tooltip}
          data-testid={testId}
        >
          {value}
        </div>
      </div>
    );
  }

  return null;
}
